import classNames from "classnames";
import { useMemo, useState } from "react";
import {
  DocumentRoundedIcon,
  ImageRoundedIcon,
  LoadingSpinnerIcon,
} from "../../AscIcons";
import AscGenericCard from "../AscGenericCard/AscGenericCard";
import styles from "./AscFileCard.module.scss";
import download from "downloadjs";
import { ReactComponent as FlipSvg } from "../../../assets/images/file_card_flip.svg";
import { AscFileType } from "@ascendnow/shared";

export interface AscFileCardProps {
  className?: string;
  fileName: string;
  fileUrl: string;
  fileType?: AscFileType;
  mimeType?: string;
  thumbnailUrl?: string;
  onDownload?: (
    downloadPayload: Record<"fileName" | "fileUrl", string> & {
      mimeType?: string;
    }
  ) => void;
  onError?: (error: any) => void;
}

export default function AscFileCard({
  className,
  fileType,
  mimeType,
  fileName,
  fileUrl,
  thumbnailUrl,
  onDownload,
  onError,
}: AscFileCardProps) {
  const [loading, setLoading] = useState(false);
  const icon = useMemo(() => {
    switch (fileType) {
      case "image":
        return (
          <ImageRoundedIcon
            data-testid="asc-file-card-image-icon"
            className={styles.icon}
          />
        );
      case "docs":
      default:
        return (
          <DocumentRoundedIcon
            data-testid="asc-file-card-document-icon"
            className={styles.icon}
          />
        );
    }
  }, [fileType]);

  function onLoadStart() {
    setLoading(true);
  }

  function onLoadEnd() {
    setLoading(false);
  }
  function handleDownloadClick() {
    if (loading) {
      return;
    }
    if (onDownload) {
      onDownload({ fileName, fileUrl, mimeType });
      return;
    }
    if (!fileUrl) {
      return;
    }

    try {
      onLoadStart();

      if (fileName && mimeType) {
        const request = new XMLHttpRequest();
        request.open("GET", fileUrl + "?cacheblock=true", true);
        request.responseType = "blob";
        request.onload = function (e: any) {
          download(e?.target?.response, fileName, mimeType);
        };
        request.onerror = onError || console.error;
        request.onloadstart = onLoadStart;
        request.onloadend = onLoadEnd;
        request.send();
        return;
      }

      download(fileUrl + "?cacheblock=true");
    } catch (error: any) {
      onError?.(error);
      console.error(error);
    } finally {
      onLoadEnd();
    }
  }
  return (
    <AscGenericCard
      title={null}
      data-testid="asc-file-card"
      className={classNames(styles.ground, className)}
      bodyClassName={styles.cardBody}
      imageClassName={styles.cardImage}
      coverImageUrl={thumbnailUrl}
      contentClassName={styles.cardContent}
      onClick={handleDownloadClick}
    >
      <div className={styles.titleContainer}>
        {loading ? <LoadingSpinnerIcon className={styles.loadingIcon} /> : icon}
        <span className={styles.name}>{fileName}</span>
      </div>
      <FlipSvg className={styles.flip} />
    </AscGenericCard>
  );
}
